import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../services/api';

// Create the context
const AgentReportContext = createContext();

export const useAgentReport = () => useContext(AgentReportContext);

export const AgentReportProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch Agent Report Overview
  const fetchAgentReportOverview = useCallback(async (statType, startDate, endDate, periodType) => {
    setLoading(true);
    try {
      const response = await api.post(`/report/agent/overview-matrics/${statType}`, {
        date_range: {
          start_date: startDate,
          end_date: endDate
        },
        period_type: periodType
      });
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error('Error fetching agent report overview:', error);
      setLoading(false);
      setError(error.message);
      throw error;
    }
  }, [setLoading, setError]);

  // Fetch Agent Sales Funnel Analytics
  const fetchAgentSalesFunnelAnalytics = useCallback(async (startDate, endDate, periodType) => {
    setLoading(true);
    try {
      const response = await api.post('/report/agent/sales-funnel-analytics', {
        date_range: {
          start_date: startDate,
          end_date: endDate
        },
        period_type: periodType
      });
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error('Error fetching sales funnel analytics:', error);
      setLoading(false);
      setError(error.message);
      throw error;
    }
  }, [setLoading, setError]);

  // Fetch Agent Call Performance Summary
  const fetchAgentCallPerformanceSummary = useCallback(async (statType, startDate, endDate, agentId = null) => {
    setLoading(true);
    try {
      const requestData = {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      };
      // Add agent_id if provided (for single agent reports)
      if (agentId) {
        requestData.agent_id = agentId;
      }
      const response = await api.post(`/report/agent/call-performance-summary/${statType}`, requestData);
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error('Error fetching call performance summary:', error);
      setLoading(false);
      setError(error.message);
      throw error;
    }
  }, [setLoading, setError]);

  // Fetch Agent Leaderboard
  const fetchAgentLeaderBoard = useCallback(async (periodType, startDate, endDate) => {
    setLoading(true);
    try {
      const requestData = { 
        period_type: periodType,
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      };
      const response = await api.post('/report/agent/leaderboard', requestData);
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error('Error fetching agent leaderboard:', error);
      setLoading(false);
      setError(error.message);
      throw error;
    }
  }, [setLoading, setError]);


  // Single Agent APIs - properly memoized with useCallback
  const fetchSingleAgentDetails = useCallback(async (agentId) => {
    try {
      const response = await api.get(`/report/agent/${agentId}/details/`);
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent details:', error);
      throw error;
    }
  }, [setError]);

  // Single Agent APIs
  const fetchSingleAgentOverview = useCallback(async (agentId, statType, startDate, endDate, periodType) => {
    try {
      const response = await api.post(`/report/agent/${agentId}/performance-report/overview/`, {
        date_range: {
          start_date: startDate,
          end_date: endDate
        },
        period_type: periodType,
        stat_type: statType
      });
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent overview:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentProductivity = useCallback(async (agentId, startDate, endDate) => {
    try {
      const response = await api.post(`/report/agent/${agentId}/performance-report/productivity/`, {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      });
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent productivity:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentSalesFunnel = useCallback(async (agentId, startDate, endDate) => {
    try {
      const response = await api.post(`/report/agent/${agentId}/performance-report/sales-funnel/`, {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      });
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent sales funnel:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentMonthlyPerformanceTrends = useCallback(async (agentId) => {
    try {
      const response = await api.post(`/report/agent/${agentId}/performance-report/booking-trends/`, {});
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent monthly performance trends:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentDeals = useCallback(async (agentId, startDate, endDate, statType) => {
    try {
      const response = await api.post(`/report/agent/${agentId}/deals/${statType}/`, {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      });
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent deal distribution:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentLeadsSalesFunnel = useCallback(async (agentId, startDate, endDate) => {
    try {
      // Use the same sales-funnel endpoint as it includes bookings data
      const response = await api.post(`/report/agent/${agentId}/performance-report/sales-funnel/`, {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      });
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent leads sales funnel:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentLeadPerformance = useCallback(async (agentId, startDate, endDate) => {
    try {
      const response = await api.post(`/report/agent/${agentId}/performance-report/lead-performance/`, {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      });
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent lead performance:', error);
      throw error;
    }
  }, [setError]);


const fetchSingleAgentLeadsAssigned = useCallback(async (agentId, startDate, endDate, page = 1, page_size = 10, search = '', sort_field = 'updated_at', sort_order = 'desc', funnel_filter = null) => {
    try {
      const response = await api.post(`/report/agent/${agentId}/leads/assigned/`, {
        date_range: {
          start_date: startDate,
          end_date: endDate
        },
        page: page || 1,
        page_size: page_size || 10,
        search: search || '',
        sort_field: sort_field || 'updated_at',
        sort_order: sort_order || 'desc',
        funnel_filter: funnel_filter || null
      });
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent assigned leads:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentLeadsFunnelOverview = useCallback(async (agentId, startDate, endDate) => {
    try {
      const response = await api.post(`/report/agent/${agentId}/leads/funnel-overview/`, {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      });
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent leads funnel overview:', error);
      throw error;
    }
  }, [setError]);


  const fetchSingleAgentSiteVisits = useCallback(async (agentId, statType, startDate = null, endDate = null, page = 1, pageSize = 10, search = '', sortField = 'start_time', sortOrder = 'desc', statusFilter = null) => {
    try {
      const requestData = {
        page: page || 1,
        page_size: pageSize || 10,
        search: search || '',
        sort_field: sortField || 'start_time',
        sort_order: sortOrder || 'desc'
      };
      
      if (startDate && endDate) {
        requestData.date_range = {
          start_date: startDate,
          end_date: endDate
        };
      }
      
      if (statusFilter && statusFilter !== 'all') {
        requestData.status_filter = statusFilter;
      }
      
      const response = await api.post(`/report/agent/${agentId}/sitevisits/${statType}/`, requestData);
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent site visits:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentBookings = useCallback(async (agentId, statType, startDate = null, endDate = null, page = 1, pageSize = 10, search = '', sortField = 'booking_date', sortOrder = 'desc', statusFilter = null) => {
    try {
      const requestData = {
        page: page || 1,
        page_size: pageSize || 10,
        search: search || '',
        sort_field: sortField || 'booking_date',
        sort_order: sortOrder || 'desc'
      };
      
      if (startDate && endDate) {
        requestData.date_range = {
          start_date: startDate,
          end_date: endDate
        };
      }
      
      if (statusFilter && statusFilter !== 'all') {
        requestData.status_filter = statusFilter;
      }
      
      const response = await api.post(`/report/agent/${agentId}/bookings/${statType}/`, requestData);
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent bookings:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentAttendanceSummary = useCallback(async (agentId, startDate = null, endDate = null) => {
    try {
      // Use a default stat_type for the URL, but send date range in body
      const statType = 'this_month'; // Default stat_type for URL compatibility
      const requestData = {};
      
      if (startDate && endDate) {
        requestData.date_range = {
          start_date: startDate,
          end_date: endDate
        };
      }
      
      const response = await api.post(`/report/agent/${agentId}/attendance/${statType}/summary/`, requestData);
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent attendance summary:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentAttendanceList = useCallback(async (agentId, statType, startDate = null, endDate = null, page = 1, pageSize = 10, search = '', sortField = 'date', sortOrder = 'desc') => {
    try {
      const requestData = {
        page: page || 1,
        page_size: pageSize || 10,
        search: search || '',
        sort_field: sortField || 'date',
        sort_order: sortOrder || 'desc'
      };
      
      if (startDate && endDate) {
        requestData.date_range = {
          start_date: startDate,
          end_date: endDate
        };
      }
      
      const response = await api.post(`/report/agent/${agentId}/attendance/${statType}/list/`, requestData);
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent attendance list:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentLocationTracking = useCallback(async (agentId, date) => {
    try {
      const response = await api.post(`/report/agent/${agentId}/attendance/location-tracking/`, { date });
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent location tracking:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentLocationHistory = useCallback(async (agentId, includeAddress = false) => {
    try {
      const response = await api.post(`/report/agent/${agentId}/attendance/location-history/`, {
        include_address: includeAddress
      });
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent location history:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentLocationDetails = useCallback(async (agentId, specificDate, includeAddress = true) => {
    try {
      const response = await api.post(`/report/agent/${agentId}/attendance/location-history/`, {
        specific_date: specificDate,
        include_address: includeAddress
      });
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent location details:', error);
      throw error;
    }
  }, [setError]);


  const fetchSingleAgentRecentCalls = useCallback(async (agentId, startDate = null, endDate = null, page = 1, pageSize = 100) => {
    try {
      let url = `/report/agent/${agentId}/recent-calls/`;
      const params = new URLSearchParams();
      if (startDate) {
        params.append('start_date', startDate);
      }
      if (endDate) {
        params.append('end_date', endDate);
      }
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent recent calls:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentTasks = useCallback(async (agentId, page = 1, pageSize = 25, search = '', status = null, priority = null) => {
    try {
      const requestData = {
        page: page || 1,
        page_size: pageSize || 25,
        search: search || '',
      };
      
      if (status) {
        requestData.status = status;
      }
      
      if (priority) {
        requestData.priority = priority;
      }
      
      const response = await api.post(`/report/agent/${agentId}/tasks/list/`, requestData);
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent tasks:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentFollowupPerformance = useCallback(async (agentId, startDate, endDate) => {
    try {
      const response = await api.post(`/report/agent/${agentId}/performance-report/followup-performance/`, {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      });
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent followup performance:', error);
      throw error;
    }
  }, [setError]);

  const fetchSingleAgentAIInsights = useCallback(async (agentId) => {
    try {
      const response = await api.get(`/report/agent/${agentId}/ai-insights/`);
      return response.data;
    } catch (error) {
      setError(error);
      console.error('Error fetching single agent AI insights:', error);
      throw error;
    }
  }, [setError]);


  // Context value
  const value = {
    loading,
    error,
    fetchAgentReportOverview,
    fetchAgentSalesFunnelAnalytics,
    fetchAgentCallPerformanceSummary,
    fetchAgentLeaderBoard,

    // Single Agent APIs
    fetchSingleAgentDetails,
    fetchSingleAgentOverview,
    fetchSingleAgentProductivity,
    fetchSingleAgentSalesFunnel,
    fetchSingleAgentMonthlyPerformanceTrends,
    fetchSingleAgentDeals,
    fetchSingleAgentLeadsSalesFunnel,
    fetchSingleAgentLeadPerformance,
    fetchSingleAgentLeadsAssigned,
    fetchSingleAgentLeadsFunnelOverview,
    fetchSingleAgentSiteVisits,
    fetchSingleAgentBookings,
    fetchSingleAgentAttendanceSummary,
    fetchSingleAgentAttendanceList,
    fetchSingleAgentLocationTracking,
    fetchSingleAgentLocationHistory,
    fetchSingleAgentLocationDetails,
    fetchSingleAgentRecentCalls,
    fetchSingleAgentTasks,
    fetchSingleAgentFollowupPerformance,
    fetchSingleAgentAIInsights
  };

  return (
    <AgentReportContext.Provider value={value}>
      {children}
    </AgentReportContext.Provider>
  );
};

export default AgentReportProvider;